/**
 * React Query mutation hook for updating notification settings
 *
 * Handles updating notification settings with optimistic updates,
 * server refetch on failure, and cache management.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { updateNotificationSettings } from '../../api';
import type {
  UpdateNotificationSettingsPayload,
  NotificationSettingsResponse,
  NotificationSettingsOverride,
} from '../../types';
import { notificationSettingsKeys } from './useNotificationSettingsQuery';
import { extractPlatformUser } from '@/utils/auth0UserUtils';

/**
 * Enforces that actionable in-app notifications are always enabled in mutation payloads
 * This prevents any attempt to set actionable.inApp to false. Email is left as provided
 * so users can opt out of the email channel for actionable notifications.
 *
 * @param payload - Settings payload to update
 * @returns Payload with actionable.inApp forced to true
 */
function enforceActionableEnabledInPayload(
  payload: UpdateNotificationSettingsPayload
): UpdateNotificationSettingsPayload {
  const result: UpdateNotificationSettingsPayload = {
    defaults: {
      ...payload.defaults,
      actionable: { ...payload.defaults.actionable, inApp: true },
    },
  };

  if (payload.overrides) {
    result.overrides = payload.overrides.map((override) => ({
      ...override,
      settings: {
        ...override.settings,
        actionable: { ...override.settings.actionable, inApp: true },
      },
    })) as [NotificationSettingsOverride, ...NotificationSettingsOverride[]];
  }

  return result;
}

/**
 * Hook to update notification settings
 * Handles optimistic updates and cache invalidation
 *
 * Features:
 * - Optimistic UI updates (instant feedback)
 * - Server refetch on error (prevents race conditions)
 * - Cache synchronization with server response
 *
 * @returns Mutation functions and state
 */
export function useNotificationSettingsMutation() {
  const { user, getAccessTokenSilently } = useAuth();
  const queryClient = useQueryClient();
  const userUuid = extractPlatformUser(user)?.uuid ?? 'default';

  const mutation = useMutation<
    NotificationSettingsResponse,
    Error,
    UpdateNotificationSettingsPayload
  >({
    mutationFn: async (payload: UpdateNotificationSettingsPayload) => {
      // Enforce actionable notifications are always enabled before sending to API
      const enforcedPayload = enforceActionableEnabledInPayload(payload);
      // Fetch a fresh token at mutation time to handle expired sessions
      const token = await getAccessTokenSilently();
      return updateNotificationSettings(token, userUuid, enforcedPayload);
    },
    onMutate: async (newSettings) => {
      // Cancel outgoing refetches to avoid race conditions
      await queryClient.cancelQueries({
        queryKey: notificationSettingsKeys.settings(userUuid),
      });

      // Enforce actionable notifications in optimistic update as well
      const enforcedSettings = enforceActionableEnabledInPayload(newSettings);

      // Optimistically update cache with new settings
      // This gives instant UI feedback before the server responds
      queryClient.setQueryData<NotificationSettingsResponse>(
        notificationSettingsKeys.settings(userUuid),
        enforcedSettings
      );
    },
    onError: async () => {
      // Refetch from server on error instead of snapshot-based rollback
      // This prevents race conditions when multiple mutations execute concurrently
      // (snapshots can capture other mutations' optimistic states)
      await queryClient.invalidateQueries({
        queryKey: notificationSettingsKeys.settings(userUuid),
      });
    },
    // No onSuccess or onSettled handlers:
    // - On success: optimistic update is already correct, no refetch needed
    // - On error: onError already handles the refetch
    // Adding onSuccess/onSettled would cause unnecessary API calls or double-refetches

    // No automatic retries for mutations - user must explicitly retry
    retry: false,
  });

  return {
    updateSettings: mutation.mutate,
    updateSettingsAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
